import React from "react";
import Balancer from "react-wrap-balancer";
import { Box, type FlexProps } from "@chakra-ui/react";
import { BookDemoButton } from "components/event-tracking";
import { HeroCTA } from "components/hero/cta";
import { Section } from "components/section";
import { type BrandColor, type Theme, type ThemeBgColor } from "contexts/color-theme";
import { TextTitle, type TextTitleProps } from "ui/text";
import { FadeIn } from "ui/animations";
import { type BrandColorToken } from "theme/colors";
import { type ProductFeature, type ProductFeaturesProps, ProductFeatures } from "ui/product-features";
import { HeroSpotlightBackgroundEffects } from "./spotlight-background-effects";

export type ProductFeaturesHeroProps = {
  /**
   * Light themes display a 150-shade background that fades to white in the features section.
   * Dark themes display a 950-shade background in the entire hero section with a 900-shade spotlight effect at the bottom.
   * */
  theme: Theme;

  /**
   * Single color variant to use for the background effects.
   * */
  variant: BrandColor;

  /**
   * Optional "eyebrow" text that will be displayed as a chip above the title.
   */
  eyebrow?: string;

  /**
   * Main `h1` title.
   */
  title: string;

  /**
   * Description displayed with the `h1` title.
   */
  description: string;

  /**
   * Maximum width to set on the title and description.
   */
  titleMaxW?: TextTitleProps["maxW"];

  /**
   * By default, only the demo modal button will be shown. Use this to include a signup button, as well.
   */
  includeSignupAction?: boolean; // eslint-disable-line react/boolean-prop-naming

  /**
   * The animation to display in desktop views.
   */
  media: ProductFeaturesProps["media"];

  /**
   * The product features that are shown below the animation for desktop views and with
   * their corresponding images in a slideshow for mobile views.
   */
  productFeatures: ProductFeature[];

  /**
   * Additional props to pass to the `SimpleGrid` that contains the `TextColumn` features in desktop view.
   */
  featureGridProps?: ProductFeaturesProps["featureGridProps"];

  /**
   * Minimum height to set on the product feature `TextColumn`s in the mobile view slideshow.
   * Used to help prevent unexpected layout shifting when there are different lengths of copy on different tabs in the slideshow.
   */
  mobileFeatureTextMinH?: ProductFeaturesProps["mobileFeatureTextMinH"];

  /**
   * Height of the spotlight effect.
   * @default 100%
   */
  spotlightHeight?: FlexProps["height"];

  /**
   * Optional override to the default spotlight color in the background effects.
   * */
  spotlightColor?: string;
};

/**
 * A hero section primarily used on product pages to highlight specific product features
 * along with an illustrated element.
 *
 * Includes vertically-stacked elements. Displays an animation and all product features for desktop views,
 * and a slideshow of individual product features with static images on mobile views.
 */
export function ProductFeaturesHero({
  theme,
  variant,
  spotlightColor: overrideSpotlightColor,
  eyebrow,
  title,
  description,
  titleMaxW,
  includeSignupAction,
  media,
  productFeatures,
  featureGridProps,
  mobileFeatureTextMinH,
  spotlightHeight,
}: ProductFeaturesHeroProps) {
  const bgColor: ThemeBgColor = theme === "light" ? `new.${variant}.150` : `new.${variant}.950`;
  const spotlightColor =
    overrideSpotlightColor ?? (theme === "light" ? "white" : (`new.${variant}.900` as BrandColorToken));

  return (
    // Need to use two `Section`s for easy header theming support, but want just one semantic section element
    <Box as="section" pos="relative" overflow="hidden" bg={bgColor}>
      <HeroSpotlightBackgroundEffects
        theme={theme}
        bgColor={bgColor}
        spotlightColor={spotlightColor}
        spotlightHeight={spotlightHeight}
      />

      <Box pos="relative">
        <FadeIn duration={0.3}>
          <Section as="div" theme={theme} themeBgColor={bgColor} my="0" pt="biggest">
            <TextTitle
              theme={theme}
              size="xl"
              align="center"
              chip={eyebrow ? { text: eyebrow } : undefined}
              titleAs="h1"
              title={<Balancer>{title}</Balancer>}
              description={description}
              descriptionAs="h2"
              maxW={titleMaxW}
              actions={includeSignupAction ? <HeroCTA /> : <BookDemoButton />}
            />
          </Section>

          <Section as="div" theme={theme} themeBgColor={theme === "light" ? "white" : bgColor} my="0" py="biggest">
            <ProductFeatures
              isPriority
              theme={theme}
              media={media}
              productFeatures={productFeatures}
              featureGridProps={featureGridProps}
              mobileFeatureTextMinH={mobileFeatureTextMinH}
            />
          </Section>
        </FadeIn>
      </Box>
    </Box>
  );
}
